<script setup lang="ts" generic="T">
/**
 * Tree of nodes — mail folders, wiki pages, plugin files, layout structure.
 * Rows open and close (`v-model:expanded`), one or several are selected
 * (`v-model:selected`, `multiple`), and the `row` / `actions` slots replace a
 * row's content or add buttons that show on hover and focus.
 *
 * Children can load lazily: a node with `hasChildren` and no `children` calls
 * `loadChildren(node)` on its first expand and shows a loading row meanwhile.
 * `filter` keeps only matching nodes (and their ancestors) open and marks the
 * matching text.
 *
 * With `draggable` rows can be dragged onto one another — before, inside or
 * after a row, as the pointer height says; `canDrop` vetoes, `move` reports.
 * Drags from outside (mails onto a folder) land inside a row when
 * `canDropExternal` allows them and come out as `drop` with the DataTransfer;
 * `externalDropEffect` must be one the drag source allows (`move` for mails
 * dragged out of a list), or the browser refuses the drop.
 *
 * `variant="nav"` makes the tree a sidebar navigation that reads like a list
 * of NxNavItem: the selected row gets the brand tint and the accent bar, counts
 * the NxNavItem badge, and the chevron column only shows while a row can open.
 *
 * `density="compact"` is for long trees (a mailbox with thousands of folders):
 * one line per row without block padding, a narrower indent, counts as plain
 * numbers (brand-coloured on an `emphasis` node), and the `actions` slot floats
 * over the row's end on hover and focus instead of holding the row open — give
 * the buttons there `height="xs"` so they fit the row.
 *
 * A node with `selectable: false` is a container: it looks like any row, but
 * a click, Enter or Space opens or closes it instead of selecting it.
 *
 * Follows the WAI-ARIA tree pattern: one tab stop, arrows walk and open,
 * Home/End, Enter/Space select, `*` opens all siblings, typing jumps to the
 * next row starting with those letters.
 */
import { useVirtualScroll } from '../Table/composables/useVirtualScroll';
import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js';
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, useTemplateRef, watch } from 'vue';
import {
  filterKeys,
  flattenTree,
  highlightParts,
  isSelectableNode,
  isWithin,
  type NxTreeDropPosition,
  type NxTreeKey,
  type NxTreeMove,
  type NxTreeNode,
  type NxTreeRow,
} from './tree';

const props = withDefaults(
  defineProps<{
    nodes: readonly NxTreeNode<T>[];
    /** Several rows can be selected; a click toggles a row. */
    multiple?: boolean;
    /** Keeps matching nodes and their ancestors, all open, matches marked. */
    filter?: string;
    /** Fetches the children of a node that has `hasChildren` but no `children`. */
    loadChildren?: (node: NxTreeNode<T>) => Promise<NxTreeNode<T>[]>;
    /** Rows can be dragged onto each other (`move`). */
    draggable?: boolean;
    /** Veto for a move; without it every move is allowed except into itself. */
    canDrop?: (
      source: NxTreeNode<T>,
      target: NxTreeNode<T>,
      position: NxTreeDropPosition
    ) => boolean;
    /** Allows drags from outside the tree onto a row (`drop`). */
    canDropExternal?: (target: NxTreeNode<T>, data: DataTransfer) => boolean;
    /**
     * The drop effect shown for drags from outside. It has to be one the drag
     * source's `effectAllowed` permits, else the browser refuses the drop.
     * Defaults to `copy`.
     */
    externalDropEffect?: 'copy' | 'move' | 'link';
    /**
     * `nav`: sidebar navigation in the NxNavItem look (brand tint and accent
     * bar on the selected row, NxNavItem count badge, chevron column only
     * while a row can open). Defaults to `default`.
     */
    variant?: 'default' | 'nav';
    /**
     * `compact`: one line per row for long trees — no block padding, plain
     * counts, `actions` floating over the row's end. Defaults to `default`.
     */
    density?: 'default' | 'compact';
    /** Window large, already loaded trees in a bounded flex/height container.
     * Lazy trees retain their normal layout (including loading rows). */
    virtual?: boolean;
    ariaLabel?: string;
    loadingLabel?: string;
    emptyLabel?: string;
    /** Label of the expand chevron („Ausklappen“ / „Einklappen“ are announced via aria-expanded). */
    toggleLabel?: string;
  }>(),
  {
    multiple: false,
    filter: '',
    loadChildren: undefined,
    draggable: false,
    canDrop: undefined,
    canDropExternal: undefined,
    externalDropEffect: 'copy',
    variant: 'default',
    density: 'default',
    virtual: false,
    ariaLabel: undefined,
    loadingLabel: 'Lädt …',
    emptyLabel: 'Keine Einträge',
    toggleLabel: 'Ein- oder ausklappen',
  }
);

const expanded = defineModel<NxTreeKey[]>('expanded', { default: () => [] });
const selected = defineModel<NxTreeKey | NxTreeKey[] | null>('selected', { default: null });

const emit = defineEmits<{
  /** A row was selected by click or keyboard. */
  select: [node: NxTreeNode<T>];
  /** Enter or double click on a row. */
  activate: [node: NxTreeNode<T>];
  move: [move: NxTreeMove<T>];
  /** A drag from outside the tree was dropped on a row. */
  drop: [payload: { target: NxTreeNode<T>; data: DataTransfer }];
  /** `loadChildren` failed for a node. */
  'load-error': [node: NxTreeNode<T>, error: unknown];
}>();

defineSlots<{
  row?: (p: {
    node: NxTreeNode<T>;
    level: number;
    expanded: boolean;
    selected: boolean;
  }) => unknown;
  icon?: (p: { node: NxTreeNode<T>; expanded: boolean }) => unknown;
  actions?: (p: { node: NxTreeNode<T> }) => unknown;
  empty?: () => unknown;
}>();

// ── Structure ────────────────────────────────────────────────────────────────
const loaded = shallowRef(new Map<NxTreeKey, NxTreeNode<T>[]>());
const loading = ref(new Set<NxTreeKey>());
const expandedSet = computed(() => new Set(expanded.value));
const keep = computed(() => filterKeys(props.nodes, props.filter, loaded.value));
const rows = computed<NxTreeRow<T>[]>(() =>
  flattenTree(props.nodes, expandedSet.value, loaded.value, keep.value)
);
const rowIndex = computed(() => new Map(rows.value.map((r, i) => [r.node.key, i])));
const isNav = computed(() => props.variant === 'nav');
const isCompact = computed(() => props.density === 'compact');
/**
 * Leaves keep an empty chevron column so their icons line up with rows that
 * can open. The nav variant drops it while no row can open (a flat folder list
 * then lines up with the NxNavItems around it).
 */
const showLeafSpacer = computed(() => !isNav.value || rows.value.some((r) => r.expandable));

function isSelected(key: NxTreeKey): boolean {
  const s = selected.value;
  return Array.isArray(s) ? s.includes(key) : s === key;
}

async function ensureChildren(node: NxTreeNode<T>) {
  if (node.children || loaded.value.has(node.key) || !node.hasChildren || !props.loadChildren)
    return;
  if (loading.value.has(node.key)) return;
  loading.value = new Set(loading.value).add(node.key);
  try {
    const kids = await props.loadChildren(node);
    const next = new Map(loaded.value);
    next.set(node.key, kids);
    loaded.value = next;
  } catch (error) {
    emit('load-error', node, error);
  } finally {
    const next = new Set(loading.value);
    next.delete(node.key);
    loading.value = next;
  }
}

function setExpanded(node: NxTreeNode<T>, open: boolean) {
  const has = expandedSet.value.has(node.key);
  if (open === has) return;
  expanded.value = open
    ? [...expanded.value, node.key]
    : expanded.value.filter((k) => k !== node.key);
  if (open) void ensureChildren(node);
}

function toggle(row: NxTreeRow<T>) {
  if (!row.expandable) return;
  setExpanded(row.node, !row.expanded);
}

function select(node: NxTreeNode<T>) {
  if (!isSelectableNode(node)) return;
  if (props.multiple) {
    const list = Array.isArray(selected.value) ? selected.value : [];
    selected.value = list.includes(node.key)
      ? list.filter((k) => k !== node.key)
      : [...list, node.key];
  } else {
    selected.value = node.key;
  }
  emit('select', node);
}

// ── Focus & keyboard ─────────────────────────────────────────────────────────
const listRef = useTemplateRef<HTMLElement>('list');
const focusKey = ref<NxTreeKey | null>(null);
/** The row that holds the single tab stop. */
const tabKey = computed<NxTreeKey | null>(() => {
  if (focusKey.value !== null && rowIndex.value.has(focusKey.value)) return focusKey.value;
  const sel = Array.isArray(selected.value) ? selected.value[0] : selected.value;
  if (sel !== null && sel !== undefined && rowIndex.value.has(sel)) return sel;
  return rows.value[0]?.node.key ?? null;
});

const viewport = useTemplateRef<HTMLElement>('viewport');
const isVirtual = computed(() => props.virtual && !props.loadChildren);
const virtualScroll = useVirtualScroll({
  tableRef: computed(() => (isVirtual.value ? (viewport.value ?? undefined) : undefined)),
  data: rows,
  rowIdKey: (row) => `${typeof row.node.key}:${row.node.key}`,
  initialRenderLimit: computed(() => 30),
  bodyRowHeight: computed(() => (isNav.value && !isCompact.value ? '3em' : '2em')),
  rowSizing: computed(() => 'auto' as const),
  draggedRow: computed(() =>
    dragSource.value ? (rowIndex.value.get(dragSource.value.key) ?? null) : null
  ),
});
const renderedRows = computed(() =>
  isVirtual.value
    ? virtualScroll.visibleRows.value
    : rows.value.map((row, rowIdx) => ({ row, rowIdx }))
);
const hasVisibleTabStop = computed(() =>
  renderedRows.value.some(({ row }) => row.node.key === tabKey.value)
);
function measureTreeRow(el: unknown, key: NxTreeKey) {
  if (isVirtual.value) virtualScroll.measureRow(`${typeof key}:${key}`, el as HTMLElement | null);
}

async function focusRow(key: NxTreeKey) {
  focusKey.value = key;
  const i = rowIndex.value.get(key);
  if (i === undefined) return;
  if (isVirtual.value && viewport.value) {
    const el = viewport.value;
    const top = virtualScroll.offsetOf(i);
    const bottom = virtualScroll.offsetOf(i + 1);
    if (top < el.scrollTop) el.scrollTop = top;
    else if (bottom > el.scrollTop + el.clientHeight) el.scrollTop = bottom - el.clientHeight;
    virtualScroll.scrollTop.value = el.scrollTop;
  }
  await nextTick();
  listRef.value?.querySelector<HTMLElement>(`[data-row-index="${i}"]`)?.focus();
}

let typed = '';
let typedTimer: ReturnType<typeof setTimeout> | undefined;
onBeforeUnmount(() => {
  clearTimeout(typedTimer);
  clearTimeout(hoverTimer);
});

function typeAhead(char: string, from: number) {
  clearTimeout(typedTimer);
  typed += char.toLowerCase();
  typedTimer = setTimeout(() => (typed = ''), 500);
  const n = rows.value.length;
  // A repeated single letter cycles; a longer prefix searches from the current row.
  const start = typed.length === 1 ? from + 1 : from;
  for (let step = 0; step < n; step++) {
    const row = rows.value[(start + step) % n]!;
    if (row.node.label.toLowerCase().startsWith(typed)) {
      void focusRow(row.node.key);
      return;
    }
  }
}

function onKeydown(event: KeyboardEvent, i: number) {
  // Keys pressed on a button in the `actions` slot belong to that button.
  if (event.target !== event.currentTarget) return;
  const row = rows.value[i];
  if (!row) return;
  const go = (j: number) => {
    const target = rows.value[j];
    if (target) void focusRow(target.node.key);
  };
  switch (event.key) {
    case 'ArrowDown':
      go(Math.min(i + 1, rows.value.length - 1));
      break;
    case 'ArrowUp':
      go(Math.max(i - 1, 0));
      break;
    case 'Home':
      go(0);
      break;
    case 'End':
      go(rows.value.length - 1);
      break;
    case 'ArrowRight':
      if (row.expandable && !row.expanded) setExpanded(row.node, true);
      else if (row.expanded && rows.value[i + 1]?.parentKey === row.node.key) go(i + 1);
      break;
    case 'ArrowLeft':
      if (row.expanded) setExpanded(row.node, false);
      else if (row.parentKey !== null) void focusRow(row.parentKey);
      break;
    case 'Enter':
      if (row.node.selectable === false) {
        toggle(row);
        break;
      }
      select(row.node);
      if (!row.node.disabled) emit('activate', row.node);
      break;
    case ' ':
      if (row.node.selectable === false) toggle(row);
      else select(row.node);
      break;
    case '*':
      for (const r of rows.value) {
        if (r.parentKey === row.parentKey && r.expandable) setExpanded(r.node, true);
      }
      break;
    default:
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        typeAhead(event.key, i);
        break;
      }
      return;
  }
  event.preventDefault();
}

function onClick(row: NxTreeRow<T>) {
  focusKey.value = row.node.key;
  // A container row opens and closes on click instead of being selected.
  if (row.node.selectable === false) toggle(row);
  else select(row.node);
}

function onDblclick(row: NxTreeRow<T>) {
  // Its two clicks already toggled a container row; nothing to activate.
  if (row.node.selectable === false) return;
  if (row.expandable) toggle(row);
  if (!row.node.disabled) emit('activate', row.node);
}

// ── Drag & drop ──────────────────────────────────────────────────────────────
const TREE_MIME = 'application/x-nx-tree-node';
const dragSource = shallowRef<NxTreeNode<T> | null>(null);
const dropAt = ref<{ key: NxTreeKey; position: NxTreeDropPosition } | null>(null);
let hoverTimer: ReturnType<typeof setTimeout> | undefined;
let hoverKey: NxTreeKey | null = null;

function canDrag(node: NxTreeNode<T>) {
  return props.draggable && !node.disabled && node.draggable !== false;
}

function onDragstart(event: DragEvent, row: NxTreeRow<T>) {
  if (!canDrag(row.node) || !event.dataTransfer) return;
  dragSource.value = row.node;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData(TREE_MIME, String(row.node.key));
  event.dataTransfer.setData('text/plain', row.node.label);
}

function positionFor(event: DragEvent, el: HTMLElement): NxTreeDropPosition {
  const box = el.getBoundingClientRect();
  if (!box.height) return 'inside';
  const y = (event.clientY - box.top) / box.height;
  if (y < 0.25) return 'before';
  if (y > 0.75) return 'after';
  return 'inside';
}

function allowed(
  row: NxTreeRow<T>,
  position: NxTreeDropPosition,
  data: DataTransfer | null
): boolean {
  const target = row.node;
  if (target.disabled) return false;
  const source = dragSource.value;
  if (source) {
    if (isWithin(source, target.key, loaded.value)) return false;
    return props.canDrop ? props.canDrop(source, target, position) : true;
  }
  if (!data || !props.canDropExternal || position !== 'inside') return false;
  return props.canDropExternal(target, data);
}

function onDragover(event: DragEvent, row: NxTreeRow<T>) {
  const internal = dragSource.value !== null;
  if (!internal && !props.canDropExternal) return;
  const position = internal ? positionFor(event, event.currentTarget as HTMLElement) : 'inside';
  if (!allowed(row, position, event.dataTransfer)) {
    if (dropAt.value?.key === row.node.key) dropAt.value = null;
    return;
  }
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = internal ? 'move' : props.externalDropEffect;
  }
  dropAt.value = { key: row.node.key, position };
  // Hovering inside a closed row opens it after a moment.
  if (position === 'inside' && row.expandable && !row.expanded) {
    if (hoverKey !== row.node.key) {
      clearTimeout(hoverTimer);
      hoverKey = row.node.key;
      hoverTimer = setTimeout(() => setExpanded(row.node, true), 600);
    }
  } else if (hoverKey !== null) {
    clearTimeout(hoverTimer);
    hoverKey = null;
  }
}

function onDragleave(event: DragEvent, row: NxTreeRow<T>) {
  const related = event.relatedTarget as Node | null;
  if (related && (event.currentTarget as HTMLElement).contains(related)) return;
  if (dropAt.value?.key === row.node.key) dropAt.value = null;
}

function onDrop(event: DragEvent, row: NxTreeRow<T>) {
  const at = dropAt.value;
  const source = dragSource.value;
  endDrag();
  if (!at || at.key !== row.node.key) return;
  event.preventDefault();
  if (source) {
    emit('move', { source, target: row.node, position: at.position });
  } else if (event.dataTransfer) {
    emit('drop', { target: row.node, data: event.dataTransfer });
  }
}

function endDrag() {
  dragSource.value = null;
  dropAt.value = null;
  clearTimeout(hoverTimer);
  hoverKey = null;
}

// Keep focus on a row that disappears (collapsed parent, filter) sensible.
watch(rows, () => {
  if (focusKey.value !== null && !rowIndex.value.has(focusKey.value)) focusKey.value = null;
});

const query = computed(() => props.filter.trim());
</script>

<template>
  <div
    ref="viewport"
    class="nx-tree-view"
    :class="{ 'is-nav': isNav, 'is-compact': isCompact, 'is-virtual': isVirtual }"
  >
    <ul
      v-if="rows.length"
      ref="list"
      class="nx-tree-view__list"
      role="tree"
      :aria-label="props.ariaLabel"
      :aria-multiselectable="props.multiple || undefined"
      :tabindex="isVirtual && !hasVisibleTabStop ? 0 : undefined"
      @focus.self="tabKey !== null && focusRow(tabKey)"
      @dragend="endDrag"
    >
      <li
        v-if="isVirtual && virtualScroll.virtualWindow.value.topPad"
        role="none"
        aria-hidden="true"
        :style="{ height: `${virtualScroll.virtualWindow.value.topPad}px`, flexShrink: 0 }"
      />
      <template v-for="{ row, rowIdx: i } in renderedRows" :key="row.node.key">
        <li
          :ref="(el) => measureTreeRow(el, row.node.key)"
          class="nx-tree-view__row"
          :class="{
            'is-selected': isSelected(row.node.key),
            'is-disabled': row.node.disabled,
            'is-emphasized': row.node.emphasis,
            'is-dragging': dragSource?.key === row.node.key,
            [`drop-${dropAt?.position}`]: dropAt?.key === row.node.key,
          }"
          role="treeitem"
          :data-row-index="i"
          :data-key="row.node.key"
          :tabindex="tabKey === row.node.key ? 0 : -1"
          :aria-level="row.level"
          :aria-posinset="row.posinset"
          :aria-setsize="row.setsize"
          :aria-expanded="row.expandable ? row.expanded : undefined"
          :aria-selected="isSelectableNode(row.node) ? isSelected(row.node.key) : undefined"
          :aria-disabled="row.node.disabled || undefined"
          :aria-busy="loading.has(row.node.key) || undefined"
          :draggable="canDrag(row.node) || undefined"
          :style="{ '--level': String(row.level - 1) }"
          @click="onClick(row)"
          @dblclick="onDblclick(row)"
          @keydown="onKeydown($event, i)"
          @focus="focusKey = row.node.key"
          @dragstart="onDragstart($event, row)"
          @dragover="onDragover($event, row)"
          @dragleave="onDragleave($event, row)"
          @drop="onDrop($event, row)"
        >
          <span
            v-if="isNav && isSelected(row.node.key)"
            class="nx-tree-view__bar"
            aria-hidden="true"
          />
          <span
            v-for="g in row.level - 1"
            :key="g"
            class="nx-tree-view__guide"
            :style="{ '--guide': String(g - 1) }"
            aria-hidden="true"
          />
          <span
            v-if="row.expandable"
            class="nx-tree-view__chevron"
            :class="{ 'is-open': row.expanded }"
            :title="props.toggleLabel"
            aria-hidden="true"
            @click.stop="toggle(row)"
          >
            <ChevronRight :class="isCompact ? 'nxt-icon-xs' : 'nxt-icon-sm'" />
          </span>
          <span
            v-else-if="showLeafSpacer"
            class="nx-tree-view__chevron is-leaf"
            aria-hidden="true"
          />
          <slot
            name="row"
            :node="row.node"
            :level="row.level"
            :expanded="row.expanded"
            :selected="isSelected(row.node.key)"
          >
            <span v-if="$slots.icon || row.node.icon" class="nx-tree-view__icon" aria-hidden="true">
              <slot name="icon" :node="row.node" :expanded="row.expanded">
                <component :is="row.node.icon" :class="isNav ? 'nxt-icon-md' : 'nxt-icon-sm'" />
              </slot>
            </span>
            <span class="nx-tree-view__label">
              <template v-for="(part, pi) in highlightParts(row.node.label, query)" :key="pi">
                <mark v-if="part.hit" class="nx-tree-view__hit">{{ part.text }}</mark>
                <template v-else>{{ part.text }}</template>
              </template>
            </span>
            <template v-if="row.node.count">
              <span
                class="nx-tree-view__count"
                :title="row.node.countLabel"
                :aria-hidden="row.node.countLabel ? 'true' : undefined"
              >
                {{ row.node.count }}
              </span>
              <!-- Joined to the name with a comma: „Posteingang, 3 ungelesen“. -->
              <span v-if="row.node.countLabel" class="nx-tree-view__sr-only">
                , {{ row.node.countLabel }}
              </span>
            </template>
          </slot>
          <span v-if="$slots.actions" class="nx-tree-view__actions" @click.stop @dblclick.stop>
            <slot name="actions" :node="row.node" />
          </span>
        </li>
        <li
          v-if="loading.has(row.node.key) && row.expanded"
          class="nx-tree-view__row is-loading"
          role="none"
          :style="{ '--level': String(row.level) }"
        >
          <span class="nx-tree-view__spinner" aria-hidden="true" />
          <span class="nx-tree-view__label">{{ props.loadingLabel }}</span>
        </li>
      </template>
      <li
        v-if="isVirtual && virtualScroll.virtualWindow.value.bottomPad"
        role="none"
        aria-hidden="true"
        :style="{ height: `${virtualScroll.virtualWindow.value.bottomPad}px`, flexShrink: 0 }"
      />
    </ul>
    <div v-else class="nx-tree-view__empty">
      <slot name="empty">{{ props.emptyLabel }}</slot>
    </div>
  </div>
</template>

<style scoped>
.nx-tree-view {
  --tree-indent: calc(var(--nxt-size-unit) * 1.125);
  --tree-row: var(--nxt-control-size-sm);
  /* Leading padding of a level-1 row; the guides are placed from it too. */
  --tree-inset: var(--nxt-space-2xs);

  min-width: 0;
  font-size: var(--nxt-font-size-sm);
}
.nx-tree-view.is-virtual {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overflow-anchor: none;
}
.nx-tree-view.is-virtual .nx-tree-view__list,
.nx-tree-view.is-virtual.is-nav .nx-tree-view__list {
  gap: 0;
}
.nx-tree-view.is-virtual.is-nav .nx-tree-view__row {
  /* Keep the navigation gap inside the measured virtual slot. */
  border-bottom: var(--nxt-space-2xs) solid transparent;
  background-clip: padding-box;
  flex-shrink: 0;
}
.nx-tree-view__list {
  margin: 0;
  padding: 0;
  font-size: inherit;
  list-style: none;
}
.nx-tree-view__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-height: var(--tree-row);
  padding-inline: calc(var(--tree-indent) * var(--level) + var(--tree-inset)) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-sm);
  color: var(--nxt-fg);
  cursor: default;
  outline: none;
  transition: background-color var(--nxt-motion-fast) var(--nxt-ease-out);
  user-select: none;
}
.nx-tree-view__row:hover:not(.is-loading) {
  background: var(--nxt-table-row-hover-bg);
}
.nx-tree-view__row.is-selected {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-tree-view__row:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-tree-view__row.is-disabled {
  color: var(--nxt-fg-muted);
}
.nx-tree-view__row.is-dragging {
  opacity: 0.45;
}
.nx-tree-view__row.drop-inside {
  background: var(--nxt-brand-subtle-bg);
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-brand-border);
}
.nx-tree-view__row.drop-before::before,
.nx-tree-view__row.drop-after::after {
  content: '';
  position: absolute;
  inset-inline: calc(var(--tree-indent) * var(--level) + var(--nxt-space-xs)) var(--nxt-space-xs);
  height: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  pointer-events: none;
}
.nx-tree-view__row.drop-before::before {
  top: calc(var(--nxt-hairline) * -1);
}
.nx-tree-view__row.drop-after::after {
  bottom: calc(var(--nxt-hairline) * -1);
}
.nx-tree-view__guide {
  position: absolute;
  inset-block: 0;
  inset-inline-start: calc(
    var(--tree-indent) * var(--guide) + var(--tree-inset) + var(--tree-indent) / 2
  );
  width: var(--nxt-hairline);
  background: var(--nxt-border-subtle);
  pointer-events: none;
}
.nx-tree-view__chevron {
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
  width: var(--tree-indent);
  height: var(--tree-indent);
  border-radius: var(--nxt-radius-sm);
  color: var(--nxt-fg-muted);
  cursor: pointer;
  transition:
    transform var(--nxt-motion-fast) var(--nxt-ease-out),
    background-color var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-tree-view__chevron:hover {
  background: var(--nxt-chart-track);
  color: var(--nxt-fg);
}
.nx-tree-view__chevron.is-open {
  transform: rotate(90deg);
}
.nx-tree-view__chevron.is-leaf {
  cursor: default;
}
.nx-tree-view__chevron.is-leaf:hover {
  background: none;
}
.nx-tree-view__icon {
  display: inline-flex;
  flex: 0 0 auto;
  color: var(--nxt-fg-subtle);
}
.nx-tree-view__row.is-selected .nx-tree-view__icon {
  color: inherit;
}
.nx-tree-view__label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-tree-view__hit {
  border-radius: calc(var(--nxt-hairline) * 2);
  background: var(--nxt-warning-subtle-bg);
  color: var(--nxt-warning-subtle-fg);
}
.nx-tree-view__count {
  flex: 0 0 auto;
  min-width: calc(var(--nxt-size-unit) * 1.25);
  padding: 0 var(--nxt-space-xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-chart-track);
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  font-weight: var(--nxt-font-weight-semibold);
  line-height: 1.6;
  text-align: center;
}
.nx-tree-view__row.is-selected .nx-tree-view__count {
  background: var(--nxt-brand-bg);
  color: var(--nxt-fg-on-accent);
}
.nx-tree-view__actions {
  display: inline-flex;
  flex: 0 0 auto;
  gap: var(--nxt-space-2xs);
  opacity: 0;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-tree-view__row:hover .nx-tree-view__actions,
.nx-tree-view__row:focus-within .nx-tree-view__actions,
.nx-tree-view__row.is-selected .nx-tree-view__actions {
  opacity: 1;
}
.nx-tree-view__row.is-loading {
  color: var(--nxt-fg-muted);
  font-style: italic;
}
.nx-tree-view__spinner {
  width: var(--nxt-dot-size-lg);
  height: var(--nxt-dot-size-lg);
  margin-inline-start: calc(var(--tree-indent) / 4);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-chart-track);
  border-top-color: var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-full);
  animation: nx-tree-spin 0.8s linear infinite;
}
.nx-tree-view__empty {
  padding: var(--nxt-space-md);
  color: var(--nxt-fg-muted);
  text-align: center;
}
.nx-tree-view__sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  margin: calc(var(--nxt-hairline) * -1);
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Nav variant: the rows read like NxNavItem (sidebar navigation) ─────── */
.nx-tree-view.is-nav {
  --tree-inset: var(--nxt-space-sm);
}
.nx-tree-view.is-nav .nx-tree-view__list {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}
.nx-tree-view.is-nav .nx-tree-view__row {
  min-height: 0;
  padding-block: var(--nxt-space-xs);
  border-radius: var(--nxt-radius-md);
  color: var(--nxt-fg-subtle);
  cursor: pointer;
}
.nx-tree-view.is-nav .nx-tree-view__row.is-disabled {
  color: var(--nxt-fg-muted);
  cursor: default;
}
.nx-tree-view.is-nav .nx-tree-view__row:hover:not(.is-loading, .is-selected) {
  background: color-mix(in srgb, var(--nxt-fg) 6%, transparent);
}
.nx-tree-view.is-nav .nx-tree-view__row:hover:not(.is-loading, .is-selected, .is-disabled) {
  color: var(--nxt-fg);
}
.nx-tree-view.is-nav .nx-tree-view__row.is-selected {
  background: color-mix(in srgb, var(--nxt-brand-bg) 12%, transparent);
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-tree-view.is-nav .nx-tree-view__row.is-selected:hover {
  background: color-mix(in srgb, var(--nxt-brand-bg) 16%, transparent);
}
/* The accent bar inside the selected row's leading edge (NxNavItem's). */
.nx-tree-view__bar {
  position: absolute;
  inset-inline-start: 0;
  inset-block: var(--nxt-space-xs);
  width: calc(var(--nxt-hairline) * 3);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  pointer-events: none;
}
.nx-tree-view.is-nav .nx-tree-view__row.is-selected .nx-tree-view__icon {
  color: var(--nxt-brand-bg);
}
.nx-tree-view.is-nav .nx-tree-view__count {
  min-width: calc(var(--nxt-size-unit) * 1.125);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-fg-subtle);
  line-height: inherit;
}
.nx-tree-view.is-nav .nx-tree-view__row.is-selected .nx-tree-view__count {
  background: color-mix(in srgb, var(--nxt-brand-bg) 12%, transparent);
  color: var(--nxt-brand-bg);
}
.nx-tree-view__row.is-emphasized .nx-tree-view__label {
  font-weight: var(--nxt-font-weight-semibold);
}

/* ── Compact density: one line per row, for trees of thousands of nodes ───── */
.nx-tree-view.is-compact {
  --tree-indent: calc(var(--nxt-size-unit) * 0.875);
}
.nx-tree-view.is-compact.is-nav {
  --tree-inset: var(--nxt-space-xs);
}
.nx-tree-view.is-compact .nx-tree-view__list {
  gap: 0;
}
.nx-tree-view.is-compact .nx-tree-view__row {
  min-height: var(--nxt-control-size-sm);
  padding-block: 0;
}
/* A hairline between rows keeps neighbouring tints apart, inside the measured slot. */
.nx-tree-view.is-compact.is-virtual .nx-tree-view__row {
  border-bottom-width: var(--nxt-hairline);
}
.nx-tree-view.is-compact:not(.is-virtual) .nx-tree-view__list {
  gap: var(--nxt-hairline);
}
.nx-tree-view.is-compact.is-nav .nx-tree-view__row:not(.is-disabled, .is-selected) {
  color: var(--nxt-fg);
}
.nx-tree-view.is-compact.is-nav .nx-tree-view__row.is-selected {
  color: var(--nxt-brand-bg);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-tree-view.is-compact .nx-tree-view__bar {
  inset-block: var(--nxt-space-xs);
}
.nx-tree-view.is-compact .nx-tree-view__count,
.nx-tree-view.is-compact .nx-tree-view__row.is-selected .nx-tree-view__count {
  min-width: 0;
  padding: 0;
  background: none;
  color: var(--nxt-fg-subtle);
  font-weight: var(--nxt-font-weight-medium);
}
.nx-tree-view.is-compact .nx-tree-view__row.is-emphasized .nx-tree-view__count {
  color: var(--nxt-brand-bg);
  font-weight: var(--nxt-font-weight-semibold);
}
/* The row's buttons take the count's place on hover and focus instead of
   holding a one-line row open. */
.nx-tree-view.is-compact .nx-tree-view__actions {
  position: absolute;
  inset-block: 0;
  inset-inline-end: var(--nxt-space-2xs);
  align-items: center;
}
.nx-tree-view.is-compact
  .nx-tree-view__row.is-selected:not(:hover, :focus-within)
  .nx-tree-view__actions {
  opacity: 0;
}
.nx-tree-view.is-compact
  .nx-tree-view__row:is(:hover, :focus-within):has(.nx-tree-view__actions > *)
  .nx-tree-view__count {
  visibility: hidden;
}
@keyframes nx-tree-spin {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-tree-view__row,
  .nx-tree-view__chevron,
  .nx-tree-view__actions {
    transition: none;
  }
  .nx-tree-view__spinner {
    animation-duration: 2.4s;
  }
}
@media (forced-colors: active) {
  .nx-tree-view__row.is-selected {
    outline: calc(var(--nxt-hairline) * 2) solid Highlight;
  }
}
</style>
